import { SnackbarContext } from '@/contexts/SnackbarContext';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Input from '@superfunnel/ui-core/v2/Input';
import { useCallback, useContext, useEffect, useState } from 'react';
import { UserContext } from '../contexts/UserContext';
import { createCheckoutSession } from '../services/BillingService';

interface Props {
  open: boolean;
  onClose: () => void;
  variantId: string;
}

export default function PaymentLinkModal({ open, onClose, variantId }: Props) {
  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);

  const [discountCode, setDiscountCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (!open) return;

    setDiscountCode('');
    setSaving(false);
    setError(undefined);
  }, [open]);

  const handleCreateCheckout = useCallback(async () => {
    if (saving) return;
    if (!selectedAccount) return;

    setSaving(true);
    setError(undefined);

    try {
      const response = await createCheckoutSession(
        selectedAccount.id,
        variantId,
        window.location.href,
        discountCode.trim() || undefined,
      );

      if (response.checkoutUrl) {
        onClose();
        await navigator.clipboard.writeText(response.checkoutUrl);
        show('Payment link copied to clipboard', 'success');
      } else {
        setError(new Error('No checkout URL returned'));
      }
    } catch (err) {
      console.error('Failed to create checkout:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to create checkout'),
      );
    } finally {
      setSaving(false);
    }
  }, [saving, selectedAccount, variantId, discountCode, onClose, show]);

  const actions: ModalActionData[] = [
    {
      type: 'secondary',
      title: 'Cancel',
      onClick: onClose,
      disabled: saving,
    },
    {
      type: 'primary',
      title: saving ? 'Creating...' : 'Copy Payment Link',
      onClick: handleCreateCheckout,
      loading: saving,
      disabled: saving,
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Generate Payment Link"
      description="Generate a payment link with an optional discount code"
      actions={actions}
      width="md"
    >
      <div className="flex flex-col gap-4">
        <Input
          title="Variant ID"
          value={variantId}
          disabled
          className="bg-gray-50"
        />

        <Input
          title="Discount Code (optional)"
          value={discountCode}
          onChange={(e) => setDiscountCode(e.target.value)}
          placeholder="Enter discount code"
          disabled={saving}
        />

        {error && <p className="text-sm text-red-500">{error.message}</p>}
      </div>
    </Modal>
  );
}
